import { Component, signal, OnInit, HostListener } from '@angular/core'
import { CommonModule } from '@angular/common'
import { Router, RouterModule } from '@angular/router'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { PreferencesService } from '@services/preferences.service'
import { AudioService } from '@services/audio.service'

interface NavItem {
  labelKey: string
  route: string
  icon?: string
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  mobileMenuOpen = signal(false)
  currentLang = signal('en')
  currentRoute = signal('')
  private lastScrollTop = 0

  navItems: NavItem[] = [
    { labelKey: 'nav.home', route: '/home', icon: 'home' },
    { labelKey: 'nav.services', route: '/services', icon: 'work' },
    // { labelKey: 'nav.portfolio', route: '/portfolio', icon: 'collections' },
    { labelKey: 'nav.about', route: '/about', icon: 'info' },
    { labelKey: 'nav.contact', route: '/contact', icon: 'email' },
  ]

  constructor(
    private translate: TranslateService,
    private preferences: PreferencesService,
    private router: Router,
    public audioService: AudioService,
  ) {
    this.currentLang.set(translate.currentLang || translate.defaultLang || 'en')
    this.currentRoute.set(router.url)

    // Track route changes for aria-current
    router.events.subscribe(() => {
      this.currentRoute.set(router.url)
    })
  }

  ngOnInit(): void {
    // Initialize SOS team song
    this.audioService.initAudio('assets/sos-song.mp3')
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    const currentScroll =
      window.pageYOffset || document.documentElement.scrollTop

    // Fermer le menu mobile au scroll (seuil réduit pour réactivité)
    if (
      this.mobileMenuOpen() &&
      Math.abs(currentScroll - this.lastScrollTop) > 10
    ) {
      this.closeMobileMenu()
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
  }

  /**
   * Close the mobile menu when the user presses Escape — standard keyboard
   * behaviour for dismissable overlays.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.mobileMenuOpen()) {
      this.closeMobileMenu()
      // Return focus to the toggle button so the user knows where they are
      setTimeout(() => {
        const toggle = document.querySelector<HTMLButtonElement>(
          '.mobile-menu-button',
        )
        toggle?.focus()
      }, 0)
    }
  }

  toggleMobileMenu(): void {
    const willOpen = !this.mobileMenuOpen()
    this.mobileMenuOpen.set(willOpen)

    // When opening, move focus to the first item inside the menu so keyboard
    // users jump right into the list (classic pattern for nav drawers).
    if (willOpen) {
      setTimeout(() => {
        const firstLink = document.querySelector<HTMLElement>(
          '.mobile-nav .mobile-nav-link',
        )
        firstLink?.focus()
      }, 200) // after the CSS transition
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false)
  }

  switchLanguage(lang: string): void {
    this.translate.use(lang)
    this.currentLang.set(lang)

    // Save language preference to localStorage
    this.preferences.setLanguage(lang)
  }

  toggleMusic(): void {
    this.audioService.toggle()
  }

  navigateHome(): void {
    this.router.navigate(['/home'])
  }
}
